import { emptyCourseForm, type CourseForm } from "@/lib/course-form";

const PREFIX = "imam:course-setup:v1:";
export const COURSE_SETUP_RECOVERY_CLEARED = "imam:course-setup-recovery-cleared";
export interface CourseSetupCopy {
  form: CourseForm;
  base: CourseForm;
  version: string | null;
}
const keyOf = (tenant: string, actor: string, section: string) => `${PREFIX}${tenant}:${actor}:${section}`;
function validForm(value: unknown): value is CourseForm {
  if (!value || typeof value !== "object") return false;
  const data = value as Record<string, unknown>;
  return Object.entries(emptyCourseForm("in_person")).every(([key, example]) => Array.isArray(example)
    ? Array.isArray(data[key]) && data[key].every((day: unknown) => Number.isInteger(day) && Number(day) >= 0 && Number(day) <= 6)
    : typeof data[key] === "string")
    && ["online", "in_person", "hybrid"].includes(String(data.modality))
    && ["zoom", "google_meet", "in_person", "other"].includes(String(data.meetingProvider));
}
/** Browser copies never authorize a record or replace a server read. */
export function readCourseSetupCopy(tenant: string, actor: string, section: string): CourseSetupCopy | null {
  if (!tenant || !actor) return null;
  try {
    const value = JSON.parse(localStorage.getItem(keyOf(tenant, actor, section)) ?? "null");
    if (!value || value.tenant !== tenant || value.actor !== actor || value.section !== section
      || !validForm(value.form) || !validForm(value.base)
      || !(value.version === null || typeof value.version === "string")) return null;
    return { form: value.form, base: value.base, version: value.version };
  } catch { return null; }
}
/** False means recovery is unavailable; the visible editor still retains its input. */
export function writeCourseSetupCopy(tenant: string, actor: string, section: string, copy: CourseSetupCopy | null): boolean {
  if (!tenant || !actor) return false;
  try {
    const key = keyOf(tenant, actor, section);
    if (copy) localStorage.setItem(key, JSON.stringify({ ...copy, tenant, actor, section }));
    else localStorage.removeItem(key);
    return true;
  } catch { return false; }
}
/** Explicit sign-out clears copies and stops mounted editors recreating them. */
export function clearCourseSetupRecovery() {
  window.dispatchEvent(new Event(COURSE_SETUP_RECOVERY_CLEARED));
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(PREFIX)) localStorage.removeItem(key);
    }
  } catch { /* Storage denial must never prevent sign-out. */ }
}
